/**
 * Advanced Support Resistance Zones — Dashboard Overlay
 *
 * Top-right "Advanced S/R" info table, replicating the TradingView
 * dashboard of the open-source script:
 *   - header: title + active zone count
 *   - At Price: which zone the current price sits inside
 *   - Nearest Above / Nearest Below: closest R/S levels + distance in ATRs
 *   - Strongest Shelf: zone with the highest touch count
 *   - Structure State: plain-English summary
 *
 * Positioned in the chart's top-right corner (clear of the price scale
 * via the priceScaleWidth prop).
 * CSS prefix: asrz-
 */
import React from "react";
import { AsrzDashboard, asrzFormatPrice } from "../../indicators/built-in/advanced-support-resistance-zones";
import "./SupportResistanceZonesOverlay.scss";

interface SupportResistanceZonesOverlayProps {
  dashboard: AsrzDashboard | null;
  priceScaleWidth: number;
  visible: boolean;
  showDashboard: boolean;
  onToggleDashboard: () => void;
}

const SupportResistanceZonesOverlay: React.FC<SupportResistanceZonesOverlayProps> = ({
  dashboard, priceScaleWidth, visible, showDashboard, onToggleDashboard,
}) => {
  if (!visible) return null;

  const d = dashboard;
  const fmt = asrzFormatPrice;
  const right = (priceScaleWidth > 0 ? priceScaleWidth : 62) + 14;

  return (
    <div className="asrz-dash" style={{ right }}>
      {showDashboard && d ? (
        <>
          <div className="asrz-head">
            <span className="asrz-title">Advanced S/R</span>
            <span className="asrz-count">{d.activeZones} active zones</span>
            <button className="asrz-toggle" onClick={onToggleDashboard} title="Collapse">▲</button>
          </div>

          <div className="asrz-row">
            <span className="asrz-label">At Price</span>
            <span className="asrz-value asrz-cyan">
              {d.atPriceInside === "res"
                ? `Inside Res @${fmt(d.currentPrice)}`
                : d.atPriceInside === "sup"
                ? `Inside Sup @${fmt(d.currentPrice)}`
                : "Between zones"}
            </span>
          </div>

          <div className="asrz-row">
            <span className="asrz-label">Nearest Above</span>
            <span className="asrz-value asrz-red">
              {d.nearestAbove
                ? `R ${fmt(d.nearestAbove.price)} (${d.nearestAbove.atr.toFixed(1)} ATR)`
                : "—"}
            </span>
          </div>

          <div className="asrz-row">
            <span className="asrz-label">Nearest Below</span>
            <span className="asrz-value asrz-green">
              {d.nearestBelow
                ? `S ${fmt(d.nearestBelow.price)} (${d.nearestBelow.atr.toFixed(1)} ATR)`
                : "—"}
            </span>
          </div>

          <div className="asrz-row">
            <span className="asrz-label">Strongest Shelf</span>
            <span className="asrz-value asrz-orange">
              {d.strongest
                ? `${d.strongest.type} ${d.strongest.touches}x @${fmt(d.strongest.price)}`
                : "—"}
            </span>
          </div>

          <div className="asrz-row">
            <span className="asrz-label">Structure State</span>
            <span className="asrz-value asrz-cyan">{d.structureState}</span>
          </div>
        </>
      ) : (
        <div className="asrz-head">
          <span className="asrz-title">Advanced S/R</span>
          <button className="asrz-toggle asrz-toggle--right" onClick={onToggleDashboard} title="Expand">▼</button>
        </div>
      )}
    </div>
  );
};

export default SupportResistanceZonesOverlay;
